import { FeatureCategories } from 'src/modules/designStudio/constants';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import type { WindowItemType } from 'src/modules/designStudio/constants/hardCodedData/windowParametricData';
import { Types } from 'src/modules/designStudio/constants/menu';
import type {
  LegacyDesignConfig,
  TrackingDesignConfig,
} from 'src/modules/designStudio/types';
import type { DesignStudioColor } from 'src/shared/graphql/generated';

export const TYPES_WHERE_COLOR_SELECTION_IS_NOT_REQUIRED = [
  Types.BRICK,
  Types.STONE,
  Types.ASPHALT,
  Types.TILE,
];

export interface ExpandedProductSelectedAttributes {
  category?: string;
  texture_color_group?: string;
  texture_name?: string;
  texture_shape?: string;
  texture_size?: string;
  texture_style?: string;
  texture_id?: number;
  texture_type?: string;
  product_type?: string;
  color_group?: string;
  color_hex?: string;
  color_name?: string;
  frame?: string;
  grid?: string;
}

export const newTrackingDesignConfig = ({
  trackingDesignConfig,
  selectedWindow,
  selectedTexture,
  selectedColor,
  category,
}: {
  trackingDesignConfig: TrackingDesignConfig;
  selectedWindow?: WindowItemType;
  selectedTexture?: MenuItemType;
  selectedColor?: DesignStudioColor;
  category: FeatureCategories;
}) => {
  const { texture: currentTexture, color: currentColor } =
    trackingDesignConfig[category];
  return {
    ...trackingDesignConfig,
    [category]: {
      texture: selectedWindow || selectedTexture || currentTexture,
      color: selectedColor || currentColor,
    },
  } as TrackingDesignConfig;
};

export const getProductSelectedAttributes = ({
  trackingDesignConfig,
  currentCategory,
  designConfig,
}: {
  trackingDesignConfig: TrackingDesignConfig;
  currentCategory: FeatureCategories;
  designConfig: LegacyDesignConfig;
}): ExpandedProductSelectedAttributes => {
  if (currentCategory === FeatureCategories.WINDOW) {
    const { texture, color } = trackingDesignConfig[currentCategory];
    const previousWindowConfig = designConfig[currentCategory];
    return {
      category: currentCategory,
      frame: texture?.frame || previousWindowConfig?.frameType || undefined,
      grid: texture?.grid || previousWindowConfig?.gridType || undefined,
      color_group: color?.group,
      color_hex: color?.color || previousWindowConfig?.color || undefined,
      color_name: color?.name,
    };
  }
  const { texture, color } = trackingDesignConfig[currentCategory];
  const previousDesignConfigCategory = designConfig[currentCategory];
  return {
    category: currentCategory,
    texture_color_group: texture?.colorGroup,
    texture_name: texture?.name,
    texture_shape: texture?.shape,
    texture_size: texture?.size,
    texture_style: texture?.style,
    texture_id:
      texture?.textureId ||
      previousDesignConfigCategory?.textureId ||
      undefined,
    texture_type: texture?.textureType,
    product_type: texture?.type,
    color_name: color?.name,
    color_group: color?.group,
    color_hex: color?.color || previousDesignConfigCategory?.color || undefined,
  };
};

/**
 * Windows are currently represented with parametric data, so
 * the grid type, the frame type, and the color all need to be
 * present in order for a window product to be considered "selected"
 */
const isWindowSelectionComplete = (
  trackingAttributes: ExpandedProductSelectedAttributes,
) =>
  !!trackingAttributes?.frame &&
  !!trackingAttributes?.grid &&
  !!trackingAttributes?.color_hex;

/**
 * There are no textures for trim currently, so only the color_hex
 * attribute is required for a trim product to be considered selected.
 */
const isTrimSelectionComplete = (
  trackingAttributes: ExpandedProductSelectedAttributes,
) => !!trackingAttributes?.color_hex;

/**
 * In most cases for Roofs and Walls, both the textureId and color need
 * to be selected in order for a product to be considered "selected".
 *
 * NOTE: There are 4 exceptions to this where the color is defined in the
 * texture data and thus these 4 textures do not require a seperate color
 * selection: brick & stone (walls), and asphalt & tile (roofs)
 */
const isRoofOrWallSelectionComplete = (
  trackingAttributes: ExpandedProductSelectedAttributes,
) =>
  TYPES_WHERE_COLOR_SELECTION_IS_NOT_REQUIRED.includes(
    trackingAttributes?.product_type as Types,
  ) ||
  (!!trackingAttributes?.color_hex && !!trackingAttributes?.texture_id);

export const isProductSelected = (
  trackingAttributes: ExpandedProductSelectedAttributes,
): boolean => {
  const { category } = trackingAttributes;
  if (category === FeatureCategories.TRIM) {
    return isTrimSelectionComplete(trackingAttributes);
  }
  if (category === FeatureCategories.WINDOW) {
    return isWindowSelectionComplete(trackingAttributes);
  }
  return isRoofOrWallSelectionComplete(trackingAttributes);
};
